import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { JobOrderDisplayType } from './types';
import { getInfoBoxEntries, splitDisplayJobIntoColumns } from './jobOrderUtils';

interface InfoBoxProps {
    jobOrderDisplayData: JobOrderDisplayType;
    isNarrow: boolean;
}

const InfoBox = ({ jobOrderDisplayData, isNarrow }: InfoBoxProps) => {
    const { t } = useTranslation();
    const { twoColumnEntries, singleColumnEntries } = getInfoBoxEntries(jobOrderDisplayData);
    const { displayTwoColumnEntries, displaySingleColumnEntries, leftColumn, rightColumn } =
        splitDisplayJobIntoColumns(isNarrow, twoColumnEntries, singleColumnEntries);

    if (displayTwoColumnEntries.length === 0 && displaySingleColumnEntries.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                padding: 2,
                marginBottom: 2,
                backgroundColor: '#fafafa',
            }}
        >
            {/* Two-column layout */}
            {displayTwoColumnEntries.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isNarrow ? 'column' : 'row',
                        gap: isNarrow ? 1 : 3,
                        marginBottom: displaySingleColumnEntries.length > 0 ? 2 : 0,
                    }}
                >
                    {/* Left column */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {leftColumn.map(([key, value]) => (
                            <Box
                                key={key}
                                sx={{
                                    marginBottom: 1,
                                    display: 'flex',
                                    gap: 1,
                                    flexDirection: isNarrow ? 'column' : 'row',
                                    alignItems: isNarrow ? 'flex-start' : 'baseline',
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 'bold',
                                        minWidth: isNarrow ? 'auto' : 'fit-content',
                                        flexShrink: 0,
                                    }}
                                >
                                    {t(`web_app.r_one.infobox.${key}`)}:
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        wordBreak: 'break-word',
                                        flex: 1,
                                        minWidth: 0,
                                        color: value === null ? '#999999' : 'inherit',
                                        fontStyle: value === null ? 'italic' : 'normal',
                                    }}
                                >
                                    {value ?? '—'}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Right column */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {rightColumn.map(([key, value]) => (
                            <Box
                                key={key}
                                sx={{
                                    marginBottom: 1,
                                    display: 'flex',
                                    gap: 1,
                                    flexDirection: isNarrow ? 'column' : 'row',
                                    alignItems: isNarrow ? 'flex-start' : 'baseline',
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 'bold',
                                        minWidth: isNarrow ? 'auto' : 'fit-content',
                                        flexShrink: 0,
                                    }}
                                >
                                    {t(`web_app.r_one.infobox.${key}`)}:
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        wordBreak: 'break-word',
                                        flex: 1,
                                        minWidth: 0,
                                        color: value === null ? '#999999' : 'inherit',
                                        fontStyle: value === null ? 'italic' : 'normal',
                                    }}
                                >
                                    {value ?? '—'}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Single-column layout for long content */}
            {displaySingleColumnEntries.length > 0 && (
                <Box>
                    {displaySingleColumnEntries.map(([key, value]) => (
                        <Box key={key} sx={{ marginBottom: 1 }}>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 'bold', marginBottom: 0.5 }}
                            >
                                {t(`web_app.r_one.infobox.${key}`)}:
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    marginLeft: 1,
                                    color: value === null ? '#999999' : 'inherit',
                                    fontStyle: value === null ? 'italic' : 'normal',
                                }}
                            >
                                {value ?? '—'}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default InfoBox;
